// ── Exam computation engine, part 1: scale, grade, rounding, pass policy ─────
//
// ONE definition of "what grade is this percentage" and "did this pupil pass",
// imported by the API (`apps/api/src/lib/grade-scale.ts`,
// `report-card-rows.ts`) and the web (`apps/web/src/lib/grade-scale.ts`,
// `grade-aggregate.ts`). Before this file the rule was written at least six
// times with different answers (brief WP-2, findings E-06/E-07/E-10): a pupil
// on 35/100 with no exam pass mark was "failed" by the results summary (40%),
// "PASS" on the teacher report card (33%) and graded off the lowest scale band
// by the PDF; 89.5% was A+ on screen (integer rounding before grading) and A
// on the PDF (2dp). The same trap family as fee heads (traps-and-gaps §15.4:
// "the same rule written four times drifts").
//
// Rules this file owns (owner decisions, 2026-09-30):
//   1. A grade is resolved from the UNROUNDED percentage. Display rounding is
//      presentation only — `roundPct` is the one rounding helper and nothing
//      may round before it calls `resolveGrade`/`gradeForMarks`.
//   2. A subject passes when marks >= the exam's own `passMark` (absolute
//      marks) if the exam sets one, otherwise when its percentage clears the
//      policy's subject minimum (default 33%).
//   3. The overall result is FAIL when ANY subject fails OR the aggregate is
//      below the aggregate minimum (default 33%). A FAIL names the subjects.
//   4. ABSENT counts the subject's max in the aggregate denominator
//      (CAMP-2026-0354) and FAILS that subject unless the policy exempts it.
//
// Pure, I/O-free and dependency-light (zod only) so both apps can import it
// and so `tsc` can emit it for the plain-Node API runtime. No imports other
// than zod; relative imports in this package need the `.js` suffix.

import { z } from "zod";

// ── Rounding ────────────────────────────────────────────────────────────────

/**
 * The ONE rounding helper for marks/percentages. Half rounds up, and the
 * `EPSILON` nudge makes 1.005 → 1.01 the way a person reads it rather than
 * the way IEEE-754 stores it. Use it for DISPLAY only; never feed its output
 * to a grade or pass decision (use `gradingPct` / the exact value instead).
 */
export function roundPct(value: number, dp = 2): number {
  if (!Number.isFinite(value)) return 0;
  const f = 10 ** dp;
  return Math.round((value + Number.EPSILON) * f) / f;
}

/**
 * Percentage as used for grade bands, pass checks and ranking: the exact
 * value with only float noise removed (89.99999999999999 → 90). It is NOT a
 * display rounding — 89.5 stays 89.5, so it grades as A, not A+.
 */
export function gradingPct(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 1e6) / 1e6;
}

/** Exact percentage (`obtained * 100 / max`); `null` when `max` is not > 0. */
export function exactPercentage(obtained: number, max: number): number | null {
  if (!Number.isFinite(obtained) || !Number.isFinite(max) || max <= 0) return null;
  return gradingPct((obtained * 100) / max);
}

// ── Scale ───────────────────────────────────────────────────────────────────

export interface GradeBand {
  /** Letter shown to users, e.g. "A+". */
  grade: string;
  /** Inclusive percentage floor for this band (0–100). */
  min: number;
  /** Hex swatch used for chips/tiles. */
  color: string;
  /** Optional descriptor shown on the setup cards. */
  desc?: string;
  /** Optional grade point printed on report cards. See `gradePointFor`. */
  gp?: number;
}

// The 7-band table the Setup tab has always displayed.
export const DEFAULT_GRADE_SCALE: GradeBand[] = [
  { grade: "A+", min: 90, color: "#10b981", gp: 10, desc: "Outstanding performance indicating mastery of concepts." },
  { grade: "A",  min: 80, color: "#059669", gp: 9,  desc: "Excellent understanding and clear mastery of topics." },
  { grade: "B+", min: 70, color: "#6366f1", gp: 8,  desc: "Very good grade showing strong ability and work ethic." },
  { grade: "B",  min: 60, color: "#4f46e5", gp: 7,  desc: "Good performance meeting general target capabilities." },
  { grade: "C",  min: 50, color: "#d97706", gp: 6,  desc: "Average level meeting baseline curriculum goals." },
  { grade: "D",  min: 33, color: "#ea580c", gp: 5,  desc: "Passing grade requiring focus and practice to advance." },
  // A fail carries no grade point — the same convention CBSE uses for E1/E2.
  { grade: "F",  min: 0,  color: "#dc2626", gp: 0,  desc: "Failing grade requiring attention and direct review." },
];

export const gradeBandSchema = z.object({
  grade: z.string().trim().min(1).max(16),
  min: z.number().min(0).max(100),
  color: z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Must be a hex colour"),
  desc: z.string().max(240).optional(),
  gp: z.number().min(0).max(100).optional(),
});

// Kept liberal (≤20 bands) so schools can add CBSE A1/A2… without a schema change.
export const gradeScaleSchema = z.array(gradeBandSchema).min(1).max(20);

/** Descending copy (highest floor first) — the order every resolver relies on. */
export function sortScaleDesc(scale: readonly GradeBand[]): GradeBand[] {
  return [...scale].sort((a, b) => b.min - a.min);
}

/**
 * Coerce an untrusted value (from the settings blob) into a valid, descending
 * scale. Falls back to DEFAULT_GRADE_SCALE when missing or malformed so a
 * corrupt write can never blank out grades app-wide.
 */
export function normalizeGradeScale(raw: unknown): GradeBand[] {
  const parsed = gradeScaleSchema.safeParse(raw);
  if (!parsed.success || parsed.data.length === 0) return DEFAULT_GRADE_SCALE;
  return sortScaleDesc(parsed.data);
}

/**
 * Resolve a percentage to its band. The first band (descending) whose floor
 * the score clears wins, so a boundary score (90 with a 90-floor A+) lands in
 * the higher band; falls through to the lowest band. `pct` is expected to be
 * UNROUNDED — 89.5 is an A on the default scale.
 */
export function resolveGrade(pct: number, scale: readonly GradeBand[] = DEFAULT_GRADE_SCALE): GradeBand {
  const sorted = scale.length ? sortScaleDesc(scale) : DEFAULT_GRADE_SCALE;
  const p = gradingPct(pct);
  for (const band of sorted) {
    if (p >= band.min) return band;
  }
  return sorted[sorted.length - 1]!;
}

/** Convenience: just the letter for a percentage. */
export function gradeLetter(pct: number, scale: readonly GradeBand[] = DEFAULT_GRADE_SCALE): string {
  return resolveGrade(pct, scale).grade;
}

/**
 * Grade letter straight from marks, never through a rounded percentage.
 * `null` when there is nothing to grade (no marks, or a non-positive max).
 */
export function gradeForMarks(
  marks: number | null | undefined,
  max: number,
  scale: readonly GradeBand[] = DEFAULT_GRADE_SCALE,
): string | null {
  if (marks === null || marks === undefined) return null;
  const pct = exactPercentage(marks, max);
  return pct === null ? null : gradeLetter(pct, scale);
}

/**
 * Threshold-table lookup (`thresholds` sorted high→low; first `pct >= min`
 * wins). Kept for the Gradebook's report-card-style tables; new code should
 * use `resolveGrade` with the school's scale.
 */
export interface GradeThreshold {
  min: number;
  grade: string;
}

export function gradeFromPct(pct: number, thresholds: readonly GradeThreshold[], fallback = "F"): string {
  const p = gradingPct(pct);
  for (const t of thresholds) {
    if (p >= t.min) return t.grade;
  }
  return fallback;
}

/**
 * Grade point for the band at `index` in a DESCENDING scale. An explicit `gp`
 * on the band wins; otherwise the top band is 10 and each step loses a point,
 * except the fail band (floor 0) which is 0 (CAMP-2026-0512).
 */
export function gradePointFor(scale: readonly GradeBand[], index: number): number {
  const sorted = sortScaleDesc(scale.length ? scale : DEFAULT_GRADE_SCALE);
  const band = sorted[index];
  if (!band) return 0;
  if (typeof band.gp === "number") return band.gp;
  const isFailBand = index === sorted.length - 1 && band.min <= 0;
  if (isFailBand) return 0;
  return Math.max(1, 10 - index);
}

/** Band + its descending index + grade point, in one call. */
export function resolveGradeWithPoint(
  pct: number,
  scale: readonly GradeBand[] = DEFAULT_GRADE_SCALE,
): { band: GradeBand; index: number; grade: string; gp: number } {
  const sorted = sortScaleDesc(scale.length ? scale : DEFAULT_GRADE_SCALE);
  const p = gradingPct(pct);
  let index = sorted.length - 1;
  for (let i = 0; i < sorted.length; i++) {
    if (p >= sorted[i]!.min) { index = i; break; }
  }
  const band = sorted[index]!;
  return { band, index, grade: band.grade, gp: gradePointFor(sorted, index) };
}

/** Printable legend strip: one column per band, highest first. */
export function gradeLegend(
  scale: readonly GradeBand[] = DEFAULT_GRADE_SCALE,
): Array<{ range: string; grade: string; gp: number }> {
  const sorted = sortScaleDesc(scale.length ? scale : DEFAULT_GRADE_SCALE);
  return sorted.map((band, i) => {
    const upper = i === 0 ? 100 : Math.max(band.min, sorted[i - 1]!.min - 1);
    const isFailBand = i === sorted.length - 1 && band.min <= 0;
    return {
      range: isFailBand && upper > 0 ? `0-${upper}` : `${band.min}-${upper}`,
      grade: band.grade,
      gp: gradePointFor(sorted, i),
    };
  });
}

/** Human-readable range for band `index` of a descending scale. */
export function bandRangeLabel(scale: readonly GradeBand[], index: number): string {
  const sorted = sortScaleDesc(scale);
  const band = sorted[index];
  if (!band) return "";
  if (index === sorted.length - 1) {
    const above = sorted[index - 1];
    if (above) return `Below ${above.min}%`;
  }
  const upper = index === 0 ? 100 : Math.max(band.min, sorted[index - 1]!.min - 1);
  return `${band.min}% - ${upper}%`;
}

/**
 * Whether a percentage is NOT in the scale's lowest band.
 *
 * @deprecated Derives "pass" from the scale's fail band, which is a third
 * pass rule (E-06). Use `subjectPass` / `overallResult` with a `PassPolicy`.
 * Kept because `lib/report-card-pdf.ts` still calls it until WP-3a lands.
 */
export function isPassingPercentage(pct: number, scale: readonly GradeBand[] = DEFAULT_GRADE_SCALE): boolean {
  const sorted = sortScaleDesc(scale.length ? scale : DEFAULT_GRADE_SCALE);
  if (sorted.length < 2) return true;
  return gradingPct(pct) >= sorted[sorted.length - 2]!.min;
}

// ── Pass policy ─────────────────────────────────────────────────────────────

export interface PassPolicy {
  /** Per-subject minimum percentage (CBSE / most State boards: 33). */
  subjectMinPct: number;
  /** Aggregate minimum percentage across every counted subject. */
  aggregateMinPct: number;
  /** When true an ABSENT subject does not fail (it still counts in the
   *  aggregate denominator). Default false: absent = fail that subject. */
  absentExempt: boolean;
}

export const DEFAULT_PASS_POLICY: PassPolicy = {
  subjectMinPct: 33,
  aggregateMinPct: 33,
  absentExempt: false,
};

export const passPolicySchema = z.object({
  subjectMinPct: z.number().min(0).max(100).optional(),
  aggregateMinPct: z.number().min(0).max(100).optional(),
  absentExempt: z.boolean().optional(),
});

/** Coerce an untrusted/partial policy (settings blob) to a complete one. */
export function resolvePassPolicy(raw?: unknown): PassPolicy {
  const parsed = passPolicySchema.safeParse(raw ?? {});
  if (!parsed.success) return DEFAULT_PASS_POLICY;
  return {
    subjectMinPct: parsed.data.subjectMinPct ?? DEFAULT_PASS_POLICY.subjectMinPct,
    aggregateMinPct: parsed.data.aggregateMinPct ?? DEFAULT_PASS_POLICY.aggregateMinPct,
    absentExempt: parsed.data.absentExempt ?? DEFAULT_PASS_POLICY.absentExempt,
  };
}

export interface SubjectPassOptions {
  /** The pupil was marked ABSENT for this paper. */
  isAbsent?: boolean;
}

/**
 * Does one subject (or one paper) pass?
 *
 *  - `true` / `false`: a verdict.
 *  - `null`: no verdict — nothing entered yet, or absent under an exempting
 *    policy. Callers must not treat `null` as a fail.
 *
 * `examPassMark` is the exam's own pass mark in MARKS (`Exam.passMark`) and
 * wins over the policy percentage when it is a finite number >= 0.
 */
export function subjectPass(
  marks: number | null | undefined,
  max: number,
  examPassMark: number | null | undefined,
  policy: PassPolicy = DEFAULT_PASS_POLICY,
  opts: SubjectPassOptions = {},
): boolean | null {
  if (opts.isAbsent) return policy.absentExempt ? null : false;
  if (marks === null || marks === undefined || !Number.isFinite(marks)) return null;
  if (examPassMark !== null && examPassMark !== undefined && Number.isFinite(examPassMark) && examPassMark >= 0) {
    return gradingPct(marks) >= gradingPct(examPassMark);
  }
  const pct = exactPercentage(marks, max);
  if (pct === null) return null;
  return pct >= policy.subjectMinPct;
}

// ── Overall result ──────────────────────────────────────────────────────────

/** One subject's result. Callers with several assessments per subject should
 *  combine them first with `combineSubjectRows` (aggregate.ts). */
export interface SubjectResultRow {
  subject: string;
  marksObtained: number | null;
  maxMarks: number;
  isAbsent: boolean;
  /** The exam's own pass mark in marks (`Exam.passMark`), when set. */
  passMark?: number | null;
  /**
   * True when a COMPONENT of the subject (theory / practical / internal) is below
   * its own minimum (`componentPass`, scheme.ts). A subject fails then even when
   * its total clears the subject minimum (CBSE: 33% in theory AND in practical).
   */
  componentFailed?: boolean;
}

export type SubjectFailReason = "BELOW_MINIMUM" | "ABSENT" | "COMPONENT_BELOW_MINIMUM";

export interface SubjectVerdict {
  subject: string;
  /** Exact percentage; `null` when nothing entered or absent. */
  percentage: number | null;
  /** `null` = no verdict (ungraded, or absent under an exempting policy). */
  passed: boolean | null;
  reason?: SubjectFailReason;
}

export type OverallResultKind = "PASS" | "FAIL" | "PENDING";

export interface OverallResult {
  /** `PENDING` when no subject has a mark or an absence yet. */
  result: OverallResultKind;
  /** Subjects that failed, in input order. Empty on PASS / PENDING. */
  failedSubjects: string[];
  /** True when the aggregate is below the policy's aggregate minimum. */
  failedAggregate: boolean;
  totalObtained: number;
  totalMax: number;
  /** Exact aggregate percentage; `null` when `totalMax` is 0. */
  exactPercentage: number | null;
  /** 2dp display value; 0 when `totalMax` is 0. */
  percentage: number;
  subjects: SubjectVerdict[];
  /** Short printable verdict, e.g. "PASS", "FAIL in Mathematics, Hindi",
   *  "FAIL (aggregate below 33%)", "PENDING". */
  label: string;
}

/**
 * The overall verdict for a set of subject rows.
 *
 *  - every counted subject must pass (`subjectPass`), and
 *  - the aggregate (absent counts its max, ungraded excluded) must clear the
 *    policy's aggregate minimum.
 *
 * A row with no marks that is not absent is ungraded: it is ignored entirely.
 * Two rows naming the same subject (trimmed, case-insensitive) are NOT merged
 * here — pass them through `combineSubjectRows` first.
 */
export function overallResult(
  rows: readonly SubjectResultRow[],
  policy: PassPolicy = DEFAULT_PASS_POLICY,
): OverallResult {
  let totalObtained = 0;
  let totalMax = 0;
  let counted = 0;
  const subjects: SubjectVerdict[] = [];
  const failed: string[] = [];

  for (const r of rows) {
    const scored = !r.isAbsent && r.marksObtained !== null && Number.isFinite(r.marksObtained);
    if (!r.isAbsent && !scored) continue; // ungraded
    counted++;
    if (r.maxMarks > 0) totalMax += r.maxMarks;
    if (scored) totalObtained += r.marksObtained as number;

    const totalPassed = subjectPass(r.marksObtained, r.maxMarks, r.passMark, policy, { isAbsent: r.isAbsent });
    const componentFailed = r.componentFailed === true && !r.isAbsent;
    const passed = componentFailed ? false : totalPassed;
    const verdict: SubjectVerdict = {
      subject: r.subject,
      percentage: scored ? exactPercentage(r.marksObtained as number, r.maxMarks) : null,
      passed,
    };
    if (passed === false) {
      verdict.reason = r.isAbsent ? "ABSENT" : totalPassed === false ? "BELOW_MINIMUM" : "COMPONENT_BELOW_MINIMUM";
      failed.push(r.subject);
    }
    subjects.push(verdict);
  }

  const exact = totalMax > 0 ? exactPercentage(totalObtained, totalMax) : null;
  const percentage = exact === null ? 0 : roundPct(exact);

  if (counted === 0) {
    return {
      result: "PENDING", failedSubjects: [], failedAggregate: false,
      totalObtained: 0, totalMax: 0, exactPercentage: null, percentage: 0,
      subjects, label: "PENDING",
    };
  }

  const failedAggregate = exact !== null && exact < policy.aggregateMinPct;
  const fail = failed.length > 0 || failedAggregate;
  let label = "PASS";
  if (fail) {
    label = failed.length > 0
      ? `FAIL in ${failed.join(", ")}`
      : `FAIL (aggregate below ${policy.aggregateMinPct}%)`;
  }
  return {
    result: fail ? "FAIL" : "PASS",
    failedSubjects: failed,
    failedAggregate,
    totalObtained,
    totalMax,
    exactPercentage: exact,
    percentage,
    subjects,
    label,
  };
}
